import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, ServerCog, HeartHandshake, Zap } from "lucide-react";
import { WEBSITE_PACKAGES, WEBSITE_BETRIEB, WEBSITE_SUPPORT, WEBSITE_EXPRESS } from "@/config/content";
import { useSiteContent } from "@/context/SiteContent";

const EASE = [0.16, 1, 0.3, 1];
const fmtEur = (cents) =>
  (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
};

// Horizontales Carousel: Swipe auf dem Handy, Pfeile + Drag am Desktop (reines CSS scroll-snap)
// + subtiler 3D-Maus-Tilt (Desktop): die Preview wirkt wie eine Glasscheibe im Raum
const finePointer = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(pointer: fine)").matches &&
  !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Carousel({ images, pkgId }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });

  const onTiltMove = (e) => {
    if (!finePointer() || drag.current.down) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    e.currentTarget.style.setProperty("--rx", `${(-py * 3.5).toFixed(2)}deg`);
    e.currentTarget.style.setProperty("--ry", `${(px * 5).toFixed(2)}deg`);
  };
  const onTiltLeave = (e) => {
    e.currentTarget.style.setProperty("--rx", "0deg");
    e.currentTarget.style.setProperty("--ry", "0deg");
  };

  const scrollTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(images.length - 1, i));
    const target = track.children[clamped];
    if (target) {
      track.scrollTo({ left: target.offsetLeft - (track.clientWidth - target.clientWidth) / 2, behavior: "smooth" });
    }
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(track.children).forEach((child, idx) => {
      const childCenter = child.offsetLeft + child.clientWidth / 2;
      const dist = Math.abs(childCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = idx;
      }
    });
    setActive(best);
  };

  const onPointerDown = (e) => {
    const track = trackRef.current;
    if (!track) return;
    drag.current = { down: true, startX: e.clientX, startScroll: track.scrollLeft, moved: false };
  };
  const onPointerMove = (e) => {
    if (!drag.current.down || !trackRef.current) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 6) drag.current.moved = true;
    trackRef.current.scrollLeft = drag.current.startScroll - dx;
  };
  const endDrag = () => {
    drag.current.down = false;
  };

  return (
    <div className="relative w-full min-w-0 group" data-testid={`carousel-${pkgId}`} onMouseMove={onTiltMove} onMouseLeave={onTiltLeave}>
      <div
        ref={trackRef}
        onScroll={onScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        className="flex w-full overflow-x-auto snap-x snap-mandatory gap-4 cursor-grab active:cursor-grabbing select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [perspective:1400px]"
      >
        {images.map((img, i) => (
          <figure key={img.src} className="w-[86%] sm:w-[70%] md:w-full basis-[86%] sm:basis-[70%] md:basis-full snap-center shrink-0 relative [transform:rotateX(var(--rx,0deg))_rotateY(var(--ry,0deg))] transition-transform duration-200 ease-out will-change-transform">
            <div className="rounded-2xl overflow-hidden border border-black/10 bg-white shadow-[0_4px_24px_rgba(10,10,10,0.06)]">
              <div className="relative aspect-[16/10] overflow-hidden bg-black/5">
                <img
                  src={img.src}
                  alt={img.label}
                  loading="lazy"
                  draggable={false}
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute top-3 right-3 bg-ink/85 text-paper text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full">
                  {String(i + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
                </span>
              </div>
              <figcaption className="px-4 py-3.5 flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-ink">{img.label}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 shrink-0">
                  {i + 1} von {images.length}
                </span>
              </figcaption>
            </div>
          </figure>
        ))}
      </div>

      {/* Pfeile (Desktop) */}
      <div className="hidden md:flex absolute inset-y-0 items-center justify-between w-full px-3 pointer-events-none">
        <button
          onClick={() => scrollTo(active - 1)}
          aria-label="Vorheriges Bild"
          data-testid={`carousel-${pkgId}-prev`}
          className="w-11 h-11 bg-white/90 backdrop-blur border border-black/10 rounded-full flex items-center justify-center pointer-events-auto hover:bg-gold hover:border-gold transition-colors duration-300 opacity-0 group-hover:opacity-100 disabled:opacity-0"
          disabled={active === 0}
        >
          <ArrowLeft size={16} />
        </button>
        <button
          onClick={() => scrollTo(active + 1)}
          aria-label="Nächstes Bild"
          data-testid={`carousel-${pkgId}-next`}
          className="w-11 h-11 bg-white/90 backdrop-blur border border-black/10 rounded-full flex items-center justify-center pointer-events-auto hover:bg-gold hover:border-gold transition-colors duration-300 opacity-0 group-hover:opacity-100 disabled:opacity-0"
          disabled={active === images.length - 1}
        >
          <ArrowRight size={16} />
        </button>
      </div>

      {/* Punkte — Apple-Stil: aktiver Punkt als Pille */}
      <div className="flex justify-center items-center gap-2 mt-5">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            aria-label={`Bild ${i + 1}`}
            data-testid={`carousel-${pkgId}-dot-${i}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === active ? "w-7 bg-gold" : "w-2 bg-black/15 hover:bg-black/30"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function PackageBlock({ pkg, index, betriebPrice }) {
  const reversed = index % 2 === 1;
  return (
    <motion.article
      initial={{ opacity: 0, y: 70, scale: 0.93, rotateX: 6 }}
      whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
      viewport={{ once: true, margin: "80px" }}
      transition={{ duration: 0.95, ease: EASE }}
      style={{ transformPerspective: 1200 }}
      data-testid={`website-package-${pkg.id}`}
      className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center py-16 lg:py-24 border-t border-black/5"
    >
      <div className={`lg:col-span-5 min-w-0 ${reversed ? "lg:order-2" : ""} flex flex-col gap-6`}>
        <div className="flex items-center gap-4">
          <h3 className="font-display uppercase tracking-tight leading-none text-3xl sm:text-4xl lg:text-5xl">
            {pkg.name}
          </h3>
          {pkg.recommended && (
            <span className="bg-gold text-ink text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5">
              Empfohlen
            </span>
          )}
        </div>
        <p className="font-editorial italic text-xl text-gold -mt-3">{pkg.tagline}</p>
        <p className="text-base leading-relaxed text-zinc-500 max-w-lg">{pkg.desc}</p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {pkg.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-[13px] leading-snug text-zinc-700">
              <Check size={14} className="text-gold shrink-0 mt-0.5" />
              {f}
            </li>
          ))}
        </ul>
        <div className="flex items-end gap-4 pt-2">
          <p className="font-display tracking-tight text-5xl" data-testid={`website-price-${pkg.id}`}>
            {fmtEur(pkg.price)}
          </p>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 pb-2">einmalig</p>
        </div>
        <p className="text-xs text-zinc-500 -mt-3">
          + {fmtEur(betriebPrice)}/Monat Betrieb (ab Fertigstellung)
        </p>
        <div>
          <Link
            to={`/buchen/website?paket=${pkg.id}`}
            data-testid={`website-cta-${pkg.id}`}
            className="group relative overflow-hidden inline-flex items-center gap-3 bg-ink text-paper px-8 py-4 text-xs uppercase tracking-[0.25em]"
          >
            <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            <span className="relative flex items-center gap-3 group-hover:text-ink transition-colors duration-500">
              {pkg.name} wählen — {fmtEur(pkg.price)}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
          </Link>
        </div>
      </div>
      <div className={`lg:col-span-7 min-w-0 ${reversed ? "lg:order-1" : ""}`}>
        <Carousel images={pkg.images} pkgId={pkg.id} />
      </div>
    </motion.article>
  );
}

export default function Websites() {
  const { content } = useSiteContent();
  const prices = content.prices || {};
  const texts = content.texts || {};
  // Content-Store-Overrides (KI-Manager) schlagen die Defaults aus content.js
  const packages = WEBSITE_PACKAGES.map((p) => ({
    ...p,
    price: prices[`website_${p.id}`] ?? p.price,
    tagline: texts[`pkg_${p.id}_tagline`] ?? p.tagline,
    desc: texts[`pkg_${p.id}_desc`] ?? p.desc,
  }));
  const expressPrice = prices.website_express ?? WEBSITE_EXPRESS.price;
  const betriebPrice = prices.website_betrieb ?? WEBSITE_BETRIEB.price;
  const supports = WEBSITE_SUPPORT.map((s) => ({ ...s, price: prices[`support_${s.id}`] ?? s.price }));
  const premiumPkg = packages.find((p) => p.id === "premium");
  const supportPremium = supports.find((s) => s.id === "premium");
  return (
    <section
      id="webdesign"
      data-testid="websites-section"
      className="relative py-24 md:py-32 px-6 md:px-12 max-w-[1600px] mx-auto overflow-hidden"
    >
      {/* Kapitel-Kopf */}
      <div className="flex flex-col md:flex-row items-start md:items-baseline gap-6 mb-8 md:mb-12 relative z-10">
        <span className="font-editorial italic text-7xl md:text-8xl leading-none text-black/10 select-none">01</span>
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-4">Kapitel 01 — Webdesign</p>
          <h2 className="font-display uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            Websites &amp; Shops,
            <br />
            <span className="font-editorial italic normal-case tracking-normal text-gold">die arbeiten.</span>
          </h2>
        </div>
      </div>
      <p className="text-base md:text-lg leading-relaxed text-zinc-500 max-w-2xl mb-4">
        Keine Standard-Templates: Ich baue deine Website individuell — von der
        Unternehmensseite über Landingpages bis zum Online-Shop mit Buchungen,
        Zahlungen und eigenem Adminbereich. Wische durch die Beispiele und sieh
        direkt, was du bekommst.
      </p>
      <p className="text-sm text-zinc-400 mb-8">Übrigens: Diese Website hier läuft selbst auf meinem PREMIUM-System — was du hier erlebst, kannst du kaufen.</p>

      {/* Paket-Blöcke mit Carousels */}
      <div>
        {packages.map((pkg, i) => (
          <PackageBlock key={pkg.id} pkg={pkg} index={i} betriebPrice={betriebPrice} />
        ))}
      </div>

      {/* Express-Lieferung */}
      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "100px" }}
        data-testid="website-express-strip"
        className="mt-4 border-2 border-gold bg-gold/5 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5"
      >
        <span className="w-12 h-12 shrink-0 rounded-full bg-gold flex items-center justify-center">
          <Zap size={22} className="text-ink" />
        </span>
        <div className="flex-1">
          <p className="font-display uppercase tracking-tight text-xl md:text-2xl">
            Du hast es eilig? {WEBSITE_EXPRESS.label}
          </p>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            {WEBSITE_EXPRESS.tagline} {WEBSITE_EXPRESS.note}
          </p>
        </div>
        <p className="font-display tracking-tight text-3xl md:text-4xl shrink-0" data-testid="express-price">
          +{fmtEur(expressPrice)}
          <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-body font-normal mt-1">
            einmalig · zu jedem Paket
          </span>
        </p>
      </motion.div>

      {/* Betriebsgebühr — dunkler Kontrast-Block */}
      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "100px" }}
        data-testid="website-betrieb-block"
        className="mt-8 bg-ink text-paper p-8 md:p-12 grid md:grid-cols-12 gap-8 items-center"
      >
        <div className="md:col-span-2 flex md:justify-center">
          <ServerCog size={44} className="text-gold" />
        </div>
        <div className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-3">Pflicht für jede Website</p>
          <h3 className="font-display uppercase tracking-tight text-2xl md:text-3xl mb-3">
            {WEBSITE_BETRIEB.label} — {fmtEur(betriebPrice)}<span className="text-lg text-zinc-400"> / Monat</span>
          </h3>
          <p className="text-sm leading-relaxed text-zinc-400 max-w-xl">{WEBSITE_BETRIEB.note}</p>
        </div>
        <div className="md:col-span-3 md:text-right">
          <p className="font-display tracking-tight text-5xl text-gold" data-testid="betrieb-price">
            {fmtEur(betriebPrice)}
          </p>
          <p className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mt-1">
            pro Monat · automatische Abbuchung
          </p>
        </div>
      </motion.div>

      {/* Optionale Support-Abos */}
      <div className="mt-16">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-3">Optional — damit du dich um nichts kümmern musst</p>
        <h3 className="font-display uppercase tracking-tight text-2xl md:text-4xl mb-8">Support-Abos</h3>
        <div className="grid md:grid-cols-2 gap-6" data-testid="website-support-cards">
          {supports.map((s) => (
            <motion.div
              key={s.id}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "80px" }}
              data-testid={`support-card-${s.id}`}
              className={`relative p-8 flex flex-col gap-5 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(212,175,55,0.12)] ${
                s.recommended ? "border-2 border-gold" : "border border-black/10"
              }`}
            >
              {s.recommended && (
                <span className="absolute -top-3 left-8 bg-gold text-ink text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1">
                  Beliebt
                </span>
              )}
              <div className="flex items-center gap-3">
                <HeartHandshake size={20} className="text-gold" />
                <p className="font-display uppercase tracking-tight text-xl">{s.name}</p>
              </div>
              <p className="font-editorial italic text-lg text-gold -mt-3">{s.tagline}</p>
              <ul className="space-y-2.5">
                {s.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-zinc-700">
                    <Check size={14} className="text-gold shrink-0 mt-0.5" />
                    {p}
                  </li>
                ))}
              </ul>
              {s.note && <p className="text-xs text-zinc-500 border-l-2 border-gold pl-3">{s.note}</p>}
              <p className="font-display tracking-tight text-4xl mt-auto" data-testid={`support-price-${s.id}`}>
                +{fmtEur(s.price)}<span className="text-base text-zinc-500"> / Monat</span>
              </p>
            </motion.div>
          ))}
        </div>

        {/* Rechenbeispiel */}
        <p className="mt-8 text-sm text-zinc-500 max-w-2xl" data-testid="website-price-example">
          <span className="text-ink font-medium">Rechenbeispiel:</span> PREMIUM für {fmtEur(premiumPkg.price)} einmalig — danach{" "}
          {fmtEur(betriebPrice)} Betrieb + {fmtEur(supportPremium.price)} Premium-Support = <span className="text-ink font-medium">{fmtEur(betriebPrice + supportPremium.price)} / Monat</span>.
        </p>
      </div>
    </section>
  );
}
